'use client';

import React from 'react';
import {
  Command,
  createSuggestionItems,
  renderItems
} from 'novel';
import { marked } from 'marked';
import { isHeading, isParagraph, isParagraphEmpty, selectionContainsHeading } from '../utils/editor-state';
import { isAICommand, isAICommandEnabled, getAICommandTitles } from '../utils/command-registry';
import { Editor } from '@tiptap/react';
import { toast } from 'react-hot-toast';

// Define proper types for suggestion items
export interface CommandItemProps {
  editor: Editor;
  range: any; // Using any for range since we don't have access to the proper Range type from Novel
}

export interface SuggestionItem {
  title: string;
  description: string;
  searchTerms: string[];
  icon: React.ReactNode;
  command: (props: CommandItemProps) => void;
}

// Define base slash command suggestions
export const baseItems: SuggestionItem[] = [
  {
    title: "AI Prompt",
    description: "Invoke AI with a custom prompt",
    searchTerms: ["ai", "prompt", "invoke"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">🤖</span>,
    command: ({ editor, range }: CommandItemProps) => {
      // Delete the slash command text
      editor.chain().focus().deleteRange(range).run();

      // Dispatch a custom event to open the inline AI prompt
      console.log('Dispatching invoke-ai-prompt event');
      const event = new CustomEvent('invoke-ai-prompt');
      document.dispatchEvent(event);
    },
  },
  {
    title: "Generate Content",
    description: "Generate content using AI. Tip: Start with //todo: instruction for quick generation",
    searchTerms: ["generate", "ai", "content", "todo"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">⚡</span>,
    command: ({ editor, range }: CommandItemProps) => {
      // Delete the slash command text
      editor.chain().focus().deleteRange(range).run();

      // Dispatch a custom event to open the AI command dialog with the special slash-generate type
      const event = new CustomEvent('ai-command-dialog', {
        detail: {
          commandType: 'slash-generate'
        }
      });
      document.dispatchEvent(event);
    },
  },
  {
    title: "Refine Content",
    description: "Improve clarity and readability",
    searchTerms: ["refine", "improve", "clarity"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">✨</span>,
    command: ({ editor, range }: CommandItemProps) => {
      // Delete the slash command text
      editor.chain().focus().deleteRange(range).run();

      // Get the current selection or all content
      const selection = editor.state.selection;
      const hasSelection = !selection.empty;

      const contentToRefine = hasSelection
        ? editor.state.doc.textBetween(selection.from, selection.to)
        : editor.getText();

      if (!contentToRefine.trim()) {
        editor.chain().focus().insertContent('Please add some content to refine.').run();
        return;
      }

      // Dispatch a custom event to open the AI command dialog
      const event = new CustomEvent('ai-command-dialog', {
        detail: {
          commandType: 'refine',
          initialContent: contentToRefine,
          hasSelection
        }
      });
      document.dispatchEvent(event);
    },
  },
  {
    title: "Summarize Title",
    description: "Create a title for current paragraph",
    searchTerms: ["summarize", "summary", "title", "tldr"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">⭐</span>,
    command: ({ editor, range }: CommandItemProps) => {
      // Delete the slash command text
      editor.chain().focus().deleteRange(range).run();

      // Get the current content for context
      const currentContent = editor.getText();

      if (!currentContent.trim()) {
        editor.chain().focus().insertContent('Please add some content to summarize.').run();
        return;
      }

      // Dispatch a custom event to open the AI command dialog
      const event = new CustomEvent('ai-command-dialog', {
        detail: {
          commandType: 'summarize-title',
          initialContent: currentContent
        }
      });
      document.dispatchEvent(event);
    },
  },
  {
    title: "Text",
    description: "Start with plain text.",
    searchTerms: ["text", "paragraph", "normal"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg font-semibold">T</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setParagraph()
        .run();
    },
  },
  {
    title: "Generate Image",
    description: "AI-powered image matching and generation",
    searchTerms: ["generate", "image", "ai", "photo", "picture", "create"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">🎨</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).run();
      // Dispatch event to open AI-powered image selection dialog
      const event = new CustomEvent('open-ai-image-dialog');
      document.dispatchEvent(event);
    },
  },
  {
    title: "Insert Image",
    description: "Insert an image from URL",
    searchTerms: ["insert", "image", "photo", "picture", "img", "url"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">🖼️</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).run();
      const event = new CustomEvent('open-image-dialog');
      document.dispatchEvent(event);
    },
  },
  {
    title: "Insert Link",
    description: "Create a hyperlink",
    searchTerms: ["link", "url", "hyperlink", "href", "anchor"],
    icon: <span className="flex h-6 w-6 items-center justify-center text-lg">🔗</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).run();
      // Dispatch event to open link dialog
      const event = new CustomEvent('open-link-dialog');
      document.dispatchEvent(event);
    },
  },
  {
    title: "Heading 1",
    description: "Large section heading",
    searchTerms: ["h1", "header", "heading", "large"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-bold">H1</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 1 })
        .run();

      // Set cursor to the empty heading to show the placeholder
      const { state } = editor;
      const pos = state.selection.$head.pos;
      editor.commands.setTextSelection(pos);
    },
  },
  {
    title: "Heading 2",
    description: "Medium section heading",
    searchTerms: ["h2", "header", "heading", "medium"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-bold">H2</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 2 })
        .run();

      // Set cursor to the empty heading to show the placeholder
      const { state } = editor;
      const pos = state.selection.$head.pos;
      editor.commands.setTextSelection(pos);
    },
  },
  {
    title: "Heading 3",
    description: "Small section heading",
    searchTerms: ["h3", "header", "heading", "medium"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-bold">H3</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 3 })
        .run();

      // Set cursor to the empty heading to show the placeholder
      const { state } = editor;
      const pos = state.selection.$head.pos;
      editor.commands.setTextSelection(pos);
    },
  },
  {
    title: "Heading 4",
    description: "Small section heading",
    searchTerms: ["h4", "header", "heading", "small"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-bold">H4</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 4 })
        .run();

      // Set cursor to the empty heading to show the placeholder
      const { state } = editor;
      const pos = state.selection.$head.pos;
      editor.commands.setTextSelection(pos);
    },
  },
  {
    title: "Heading 5",
    description: "Extra small section heading",
    searchTerms: ["h5", "header", "heading", "tiny"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-bold">H5</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor
        .chain()
        .focus()
        .deleteRange(range)
        .setNode("heading", { level: 5 })
        .run();

      // Set cursor to the empty heading to show the placeholder
      const { state } = editor;
      const pos = state.selection.$head.pos;
      editor.commands.setTextSelection(pos);
    },
  },
  {
    title: "Bullet List",
    description: "Create a simple bullet list",
    searchTerms: ["bullet", "list", "unordered"],
    icon: <span className="flex h-6 w-6 items-center justify-center">•</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).toggleBulletList().run();
    },
  },
  {
    title: "Numbered List",
    description: "Create a numbered list",
    searchTerms: ["numbered", "list", "ordered"],
    icon: <span className="flex h-6 w-6 items-center justify-center">1.</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).toggleOrderedList().run();
    },
  },
  {
    title: "Code Block",
    description: "Add a code block",
    searchTerms: ["code", "codeblock"],
    icon: <span className="flex h-6 w-6 items-center justify-center font-mono">{ }</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).toggleCodeBlock().run();
    },
  },
  {
    title: "Quote",
    description: "Add a quote or citation",
    searchTerms: ["quote", "blockquote"],
    icon: <span className="flex h-6 w-6 items-center justify-center">&#34;</span>,
    command: ({ editor, range }: CommandItemProps) => {
      editor.chain().focus().deleteRange(range).toggleBlockquote().run();
    },
  },
  {
    title: "Create Outline",
    description: "Generate a document outline",
    searchTerms: ["outline", "schema", "structure"],
    icon: <span className="flex h-6 w-6 items-center justify-center">📋</span>,
    command: ({ editor, range }: CommandItemProps) => {
      // Delete the slash command text
      editor.chain().focus().deleteRange(range).run();

      // Get the current content for context
      const currentContent = editor.getText();

      if (!currentContent.trim()) {
        editor.chain().focus().insertContent('Please add some content to create a schema for.').run();
        return;
      }

      // Dispatch a custom event to open the AI command dialog
      const event = new CustomEvent('ai-command-dialog', {
        detail: {
          commandType: 'schema',
          initialContent: currentContent
        }
      });
      document.dispatchEvent(event);
    },
  },
];

// Using shared editor state utility functions from utils/editor-state.ts

// Export the base suggestion items for use in other components
export const suggestionItems = createSuggestionItems(baseItems);

// Function to get filtered suggestion items based on editor state
export const getFilteredSuggestionItems = (editor: Editor | null) => {
  // If editor is null, only include non-AI commands
  if (!editor) {
    const nonAiCommands = baseItems.filter(item => !isAICommand(item.title));
    return createSuggestionItems(nonAiCommands);
  }

  // Filter out disabled AI commands
  const filteredItems = baseItems.filter(item => {
    // Only filter AI commands, always show formatting commands
    if (isAICommand(item.title)) {
      return isAICommandEnabled(item.title, editor);
    }
    return true;
  });

  return createSuggestionItems(filteredItems);
};

// Raw filtered base items for reuse outside Novel suggestion renderer
export const getFilteredBaseItems = (editor: Editor | null): SuggestionItem[] => {
  if (!editor) {
    return baseItems.filter(item => !isAICommand(item.title));
  }
  return baseItems.filter(item => {
    if (isAICommand(item.title)) {
      return isAICommandEnabled(item.title, editor);
    }
    return true;
  });
};

// Export the isAICommandEnabled function from command-registry for use in other components
export { isAICommandEnabled } from '../utils/command-registry';

// Define types for the suggestion props
interface SuggestionProps {
  editor: Editor;
  range: any;
  command: (props: any) => void;
  items: any[];
  event?: KeyboardEvent;
  selectedIndex?: number;
}

// We'll use the default renderItems function directly
// This is the approach used in the working Novel test app

// Create the slash command extension with context-aware filtering
export const slashCommand = Command.configure({
  suggestion: {
    // Disable automatic '/' trigger - only use Ctrl+/ via CommandShortcut extension
    // We use a zero-width space as the trigger char because null/undefined might fallback to default '/'
    char: '\u200b',

    // Get filtered items based on editor state
    items: ({ editor }: { editor: Editor }) => getFilteredSuggestionItems(editor),

    // Use the default renderItems function directly
    // This is the approach used in the working Novel test app
    render: renderItems,
  },
});
